import { useParams, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { getDateDisplay } from '@/service/utils/getDateDisplay';
import { optionsToSelectorMapper } from '@/service/utils/optionsToSelectorMapper';
import { scrollToId, scrollToIdWithMargin } from '@/service/utils/scrollToId';
import { ECartActionType } from '@/service/state/Cart/domain';
import { FormDivider } from '@/ui/common/Dividers/FormDivider';
import { FormButton } from '@/ui/common/Buttons/FormButton';
import { SelectDropdown } from '@/ui/common/Dropdowns/SelectDropdown';
import { IconButton } from '@/ui/common/Buttons/IconButton';
import { CheckboxInput } from '@/ui/common/Inputs/CheckboxInput';
import { SpinnerLoaderSecondary } from '@/ui/common/Loaders/SpinnerLoaderSecondary';
import { ChangeIcon } from '@/ui/common/Icons/ChangeIcon';
import { ArrowLeftIcon } from '@/ui/common/Icons/ArrowLeftIcon';
import { TAddCartApiBody } from '@/domain/Cart.domain';
import { TicketsCounter } from './TicketsCounter';
import { Tips } from './Tips';
import { StickyStatusBar } from '../StickyStatusBar';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { TicketTextOption } from './TicketTextOption';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { createTicketsList } from '@/service/utils/createTicketsList';
import { TStatuses } from '@/domain/Activities.domain';
import { validateDailyStatuses } from '@/service/utils/createCalendarStatuses';
import { TScheduleId } from '@/domain/FullCalendar.domain';
import { TicketSkeleton } from '@/ui/components/AttractionsContent/core/TicketSkeleton';
import { PaymentIcons } from '@/ui/common/PaymentIcons/PaymentIcons';
import { URLs } from '@/service/constants/urls';

type TTripSettingsDialogProps = {
    tripName: string;
    agreement?: string;
    switchTime?: boolean;
    hideSwitch?: boolean;
    calendarStatus?: TStatuses;
    showCalendarStatuses?: boolean;
    bookingTax?: number;
    bookingFee?: number;
    redirectAfterAddToCart?: boolean;
    hideDate?: boolean;
};

const AGREEMENT_MIN_LENGHT = 400;

export const TicketsWidget = ({
    tripName,
    agreement,
    switchTime,
    hideSwitch,
    calendarStatus,
    showCalendarStatuses,
    redirectAfterAddToCart,
    hideDate,
}: TTripSettingsDialogProps) => {
    const { push } = useRouter();
    const { partner_slug } = useParams();
    const { suggested_gratuity, stripe_on } = usePartnerStore();

    const [showEntirePolicy, setShowEntirePolicy] = useState(false);
    const [optionsTouched, setOptionsTouched] = useState(false);
    const [currentAction, setCurrentAction] = useState<'add_to_cart' | 'buy_now'>('add_to_cart');

    const {
        selectedDayFull,
        agreementSelected,
        status,
        activityId,
        partnerSlug,
        selectedDate,
        selectedSchedule: selectedScheduleId,
        selectedTickets,
        ticketNames,
        selectedOptions,
        comment,
        gratuity,
        isCustom,
        selectOption,
        selectComment,
        selectAgreement,
        clearSelectedSchedule,
        ticketsSum,
        tipsPrice,
        totalSum,
        bookingFee,
        bookingTax,
        needTicketsNames,
        updateDay,
        termsSelected,
        selectTerms,
        enableTips,
    } = useAvailabilityStore();

    const { isLightframePage } = usePartnerStore();

    const { addCart, loading, error, clearError } = useCartStore();

    const terms = selectedDayFull?.terms;

    const addCartHasBackError =
        error.actionType === ECartActionType.ADD_ITEM_TO_CART && error.message;

    const clearBackError = useCallback(
        () => addCartHasBackError && clearError(ECartActionType.ADD_ITEM_TO_CART),
        [addCartHasBackError, clearError]
    );

    useEffect(
        () => () => {
            clearBackError();
        },
        [clearBackError]
    );

    const addToCart = useCallback(
        async (showToast?: boolean) => {
            const body: TAddCartApiBody = {
                trip_id: activityId,
                date: selectedDate,
                schedule_id: selectedScheduleId,
                tickets: selectedTickets,
                additional_options: selectedOptions,
                comment_guest: comment,
                gratuity: {
                    value: gratuity || 0,
                    is_custom: isCustom,
                },
            };
            if (needTicketsNames) body.guests = ticketNames;
            return await addCart({ partnerId: partnerSlug }, body, showToast);
        },
        [
            activityId,
            addCart,
            comment,
            gratuity,
            isCustom,
            partnerSlug,
            selectedDate,
            selectedOptions,
            selectedScheduleId,
            selectedTickets,
            ticketNames,
            needTicketsNames,
        ]
    );

    const handleAddToCart = useCallback(async () => {
        setCurrentAction('add_to_cart');
        const { success } = await addToCart(true);
        if (success.actionType === ECartActionType.ADD_ITEM_TO_CART && success.message) {
            updateDay(!redirectAfterAddToCart);
            clearSelectedSchedule();
            redirectAfterAddToCart
                ? push(`/${partner_slug}`)
                : window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addToCart]);

    const handleBuyNow = useCallback(async () => {
        setCurrentAction('buy_now');
        const { success } = await addToCart();
        if (success.actionType === ECartActionType.ADD_ITEM_TO_CART && success.message) {
            push(`/${partner_slug}/cart`);

            setTimeout(() => {
                updateDay(false);
                clearSelectedSchedule();
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addToCart, partner_slug]);

    if (typeof selectedScheduleId === 'undefined') {
        return <></>;
    }

    const selectedSchedule =
        (selectedDayFull?.schedules &&
            Object.values(selectedDayFull.schedules).find(
                (schedule) => schedule.id === selectedScheduleId
            )) ||
        selectedDayFull;
    const { tickets, maxTicketsSelected } = createTicketsList(
        selectedTickets,
        selectedSchedule as TScheduleId
    );

    const additionalOptions = selectedDayFull?.additional_options
        ? Object.values(selectedDayFull.additional_options).filter(
              ({ is_disabled }) => !+is_disabled
          )
        : undefined;
    const isLongRead = agreement && agreement.length > AGREEMENT_MIN_LENGHT;
    const isCalendarPage = !redirectAfterAddToCart;

    const dailyStatusesMarkup =
        calendarStatus && validateDailyStatuses(calendarStatus, selectedDate);

    const tipsValue = (bookingTax || bookingFee) && tipsPrice;
    const includesTaxes = !!bookingFee || !!bookingTax;

    return (
        <>
            <div className="md:min-w-full md:pl-16 md:pr-2.5 md:pt-8">
                <h4 className="tx-heading-4 mb-6 hidden text-center text-gray-1 dark:text-white sm:block md:pr-12">
                    {tripName}
                </h4>
                {!hideDate && (
                    <div
                        data-test-id="tickets-widget"
                        id="tickets-widget"
                        className="relative top-3 flex w-full items-center justify-between px-2 after:absolute after:bottom-[-16px] after:left-0 after:z-50 after:hidden after:h-[16px] after:w-full after:rotate-180 after:bg-gradient-transparency after:content-[''] after:dark:bg-gradient-transparency-dark sm:justify-between md:top-0 md:px-0 md:pr-[50px] md:after:block"
                    >
                        {!!selectedDate && (
                            <p
                                data-test-id="tickets-date"
                                className="tx-large-semibold text-gray-2 dark:text-gray-3"
                            >
                                {getDateDisplay(newDateFromString(selectedDate)) +
                                    (selectedSchedule?.time ? ` ${selectedSchedule.time}` : '')}
                            </p>
                        )}
                        {!hideSwitch && (
                            <button
                                type="button"
                                className="z-[1] inline-flex h-5 w-max flex-shrink-0 items-center rounded-full bg-gray-4 bg-opacity-70 pl-1 pr-2 dark:bg-dark-1"
                                data-test-id="switch-date-btn"
                            >
                                <ChangeIcon className="[&_path]:dark:fill-white" />
                                <span
                                    className="tx-large-medium text-gray-1 underline dark:text-white"
                                    onClick={() => {
                                        clearSelectedSchedule();
                                        isLightframePage
                                            ? scrollToId('calendar', 'nearest')
                                            : scrollToIdWithMargin('calendar');
                                    }}
                                >
                                    {`Switch ${switchTime ? 'Time' : 'Date'}`}
                                </span>
                            </button>
                        )}
                    </div>
                )}
                <div
                    className={`custom-scroll relative h-full overflow-y-auto pt-12 md:py-4 md:pb-20 md:pr-11 ${
                        isLightframePage ? 'md:max-h-[55vh]' : 'md:max-h-[70vh]'
                    } ${isCalendarPage ? 'pb-20' : 'pb-1'}`}
                    id="tickets-container"
                    data-test-id="tickets-container"
                >
                    {selectedDayFull ? (
                        tickets?.length ? (
                            <div className="w-full">
                                {showCalendarStatuses &&
                                    !!calendarStatus?.calendar?.[0]?.length && (
                                        <p
                                            className="tx-heading-4 my-5 min-h-[44px] rounded-2xl border-2 border-state-warning bg-sand py-3  text-center text-gray-1 dark:border-blue-8 dark:bg-dark-1 dark:text-white md:min-h-[56px] md:py-4 [&>a]:text-brand [&>a]:underline"
                                            data-test-id="calendar-status"
                                            dangerouslySetInnerHTML={{
                                                __html: calendarStatus.calendar.join('<br/>'),
                                            }}
                                        />
                                    )}
                                {dailyStatusesMarkup && (
                                    <p
                                        className="tx-heading-4 my-5 min-h-[44px] rounded-2xl border-2 border-state-warning bg-sand py-3  text-center text-gray-1 dark:border-blue-8 dark:bg-dark-1 dark:text-white md:min-h-[56px] md:py-4 [&>a]:text-brand [&>a]:underline"
                                        data-test-id="calendar-status"
                                        dangerouslySetInnerHTML={{
                                            __html: dailyStatusesMarkup,
                                        }}
                                    />
                                )}
                                <div className="flex w-full flex-col gap-[6px] sm:gap-3.5">
                                    <div
                                        data-test-id="tickets-list"
                                        className="relative flex w-full flex-col items-start rounded-[15px] bg-gray-15 px-[22px] pb-6 pt-5 dark:bg-blue-1 sm:items-center sm:pb-7 sm:pt-6"
                                    >
                                        <p
                                            id="tickets"
                                            className="tx-large-semibold mb-5 text-gray-1 dark:text-white md:mb-7"
                                        >
                                            {selectedDayFull.name}
                                        </p>
                                        {!!selectedSchedule?.min_tickets &&
                                            +selectedSchedule.min_tickets > 1 && (
                                                <div className="tx-medium-medium absolute -right-1 -top-2 z-10 mx-4 rounded-2xl bg-sand px-4 text-center text-gray-2 dark:bg-dark-1 dark:text-gray-3">
                                                    {`Requires ${selectedSchedule.min_tickets} ticket minimum`}
                                                </div>
                                            )}
                                        <TicketsCounter
                                            tickets={tickets}
                                            clearBackError={clearBackError}
                                            needTicketNames={needTicketsNames}
                                            maxTicketsSelected={maxTicketsSelected}
                                        />
                                    </div>
                                </div>
                                <div className="mx-auto mt-3.5 w-full px-4 sm:mt-11 sm:max-w-full md:mt-11 md:px-0">
                                    {!!additionalOptions?.length && (
                                        <div className="w-full" data-test-id="additional-options">
                                            <p
                                                id="additional_options"
                                                className="tx-large-semibold text-gray-1 dark:text-white"
                                            >
                                                Additional Options
                                            </p>
                                            {additionalOptions.map(
                                                (
                                                    {
                                                        id,
                                                        name,
                                                        required,
                                                        values,
                                                        include_decline,
                                                        tooltip,
                                                        base,
                                                        type,
                                                        has_image,
                                                    },
                                                    index
                                                ) => {
                                                    const selectedValue = selectedOptions[+id];
                                                    const options = optionsToSelectorMapper(
                                                        values,
                                                        {
                                                            decline: include_decline,
                                                            base,
                                                            ticketsSum,
                                                            required,
                                                            isSelected: !!selectedValue,
                                                        }
                                                    );
                                                    const selected =
                                                        selectedValue &&
                                                        options?.find(
                                                            ({ value }) => value === selectedValue
                                                        );
                                                    const selectedLabel = selected
                                                        ? selected?.price
                                                            ? `${selected.price} - ${selected.label}`
                                                            : selected.label
                                                        : '';

                                                    return (
                                                        <div key={name} className="mt-6 flex">
                                                            {has_image && (
                                                                <div className="hidden h-full md:block">
                                                                    <img
                                                                        src={`${URLs.image}/option/${id}/center/384x288/wr.jpg`}
                                                                        className="mr-4 h-[144px] min-w-[192px] rounded-2xl bg-gray-3 object-fill"
                                                                        alt="Main Option Image"
                                                                    />
                                                                </div>
                                                            )}
                                                            <div className="w-full">
                                                                <p
                                                                    className={`tx-large-semibold mb-2 text-gray-2 after:ml-1 dark:text-gray-3 ${
                                                                        +required
                                                                            ? 'after:ml-1 after:text-state-error after:content-["*"]'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {name}
                                                                </p>
                                                                {tooltip && (
                                                                    <div
                                                                        data-test-id="option-description"
                                                                        className="tx-medium-regular my-2 text-gray-2 dark:text-gray-3 md:hidden"
                                                                    >
                                                                        {tooltip}
                                                                    </div>
                                                                )}
                                                                {has_image && (
                                                                    <div className="mb-2 md:hidden">
                                                                        <img
                                                                            src={`${URLs.image}/option/${id}/center/384x288/wr.jpg`}
                                                                            className="mr-4 h-[144px] min-w-[192px] rounded-2xl bg-gray-3 object-fill"
                                                                            alt="Main Option Image"
                                                                        />
                                                                    </div>
                                                                )}
                                                                {type === 'select' ? (
                                                                    <SelectDropdown
                                                                        options={options}
                                                                        selected={selectedLabel}
                                                                        onSelect={(
                                                                            newValue: string
                                                                        ) => {
                                                                            selectOption(
                                                                                id,
                                                                                newValue
                                                                            );
                                                                            clearBackError();
                                                                        }}
                                                                        isRequired={!!+required}
                                                                        groupName="add-options"
                                                                        elementIndex={index}
                                                                        isTouched={optionsTouched}
                                                                    />
                                                                ) : (
                                                                    <TicketTextOption
                                                                        name={id}
                                                                        selected={selectedLabel}
                                                                        onInputOption={(
                                                                            newValue: string
                                                                        ) => {
                                                                            selectOption(
                                                                                id,
                                                                                newValue
                                                                            );
                                                                            clearBackError();
                                                                        }}
                                                                        isRequired={!!+required}
                                                                        placeholder={'Enter Text'}
                                                                        isTouched={optionsTouched}
                                                                    />
                                                                )}
                                                                {tooltip && (
                                                                    <div
                                                                        data-test-id="option-description"
                                                                        className="tx-medium-regular mt-2 hidden text-gray-2 dark:text-gray-3 md:block"
                                                                    >
                                                                        {tooltip}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    )}
                                    {enableTips && (
                                        <div
                                            id="tips"
                                            className="mt-7 w-full md:mt-8"
                                            data-test-id="tips-block"
                                        >
                                            <div className="tx-large-semibold flex w-full items-center gap-x-1 text-gray-1 dark:text-white">
                                                <p className="after:ml-1 after:text-state-error after:content-['*']">
                                                    Add tip
                                                </p>
                                                {!!tipsValue && (
                                                    <>
                                                        <p>-</p>
                                                        <p>{getFormattedPrice(tipsValue)}</p>
                                                    </>
                                                )}
                                            </div>
                                            <p className="tx-large-medium mb-2.5 mt-1 text-gray-3 dark:text-dark-1 md:ml-7 ">
                                                Show your support for the team
                                            </p>
                                            <Tips
                                                clearBackError={clearBackError}
                                                tipsList={suggested_gratuity}
                                            />
                                        </div>
                                    )}
                                    {selectedDayFull.guest_comment_field && (
                                        <div className="my-7 w-full md:mb-8 md:mt-5">
                                            <p className="tx-large-semibold text-gray-1 dark:text-white">
                                                Reservation comments
                                            </p>
                                            <textarea
                                                className="custom-scroll tx-large-medium relative mt-3.5 min-h-[80px] w-full rounded-[15px] border border-gray-3 bg-transparent py-[13px] pl-[30px] pr-[25px] text-lg text-gray-1 outline-none dark:border-dark-1 dark:text-gray-3 sm:py-[22px] md:mt-5"
                                                value={comment}
                                                onChange={(el) => {
                                                    selectComment(el.target.value);
                                                    clearBackError();
                                                }}
                                                placeholder="Anything you'd like us to know?"
                                                maxLength={1000}
                                                data-test-id="reservation-comments"
                                            />
                                        </div>
                                    )}
                                    {!!agreement && (
                                        <div className="mt-[30px] w-full sm:mt-8">
                                            <p className="tx-large-semibold text-gray-1 dark:text-white">
                                                Cancellation Policy:
                                            </p>
                                            <div
                                                className={`tx-large-regular relative mt-[6px] flex h-full w-full flex-col overflow-hidden text-gray-2 dark:text-gray-3 ${
                                                    showEntirePolicy || !isLongRead
                                                        ? 'max-h-max'
                                                        : 'max-h-[100px]'
                                                }`}
                                                data-test-id="agreement"
                                            >
                                                <div
                                                    className="tx-large-regular text-gray-2 dark:text-gray-3 [&>a]:text-brand [&>a]:underline"
                                                    dangerouslySetInnerHTML={{ __html: agreement }}
                                                ></div>
                                                {!showEntirePolicy && isLongRead && (
                                                    <div
                                                        data-test-id="policy-text"
                                                        className="absolute bottom-0 left-0 h-[57px] w-full bg-gradient-transparency dark:bg-gradient-transparency-dark"
                                                    />
                                                )}
                                            </div>
                                            {isLongRead && (
                                                <IconButton
                                                    icon={
                                                        <div className="flex h-5 w-5 items-center justify-center">
                                                            <ArrowLeftIcon
                                                                className={`h-2.5 w-2.5 [&_path]:fill-brand ${
                                                                    showEntirePolicy
                                                                        ? 'rotate-90'
                                                                        : '-rotate-90'
                                                                }`}
                                                            />
                                                        </div>
                                                    }
                                                    label={
                                                        showEntirePolicy ? 'Read less' : 'Read more'
                                                    }
                                                    className="tx-large-medium mt-1 flex-row-reverse text-brand sm:mt-2"
                                                    onClick={() => setShowEntirePolicy((pS) => !pS)}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div
                                        id="cancellation-info"
                                        className="mt-3 w-full sm:mt-8"
                                        data-test-id="cancellation-info"
                                    >
                                        <CheckboxInput
                                            className="mt-[13px] md:mt-[30px]"
                                            label={
                                                <span className="tx-large-regular text-gray-1 dark:text-white">
                                                    I Agree To The Terms Above
                                                </span>
                                            }
                                            checked={agreementSelected}
                                            handleChange={(agreement) => {
                                                selectAgreement(agreement);
                                                clearBackError();
                                            }}
                                        />
                                        {!!terms &&
                                            terms.map((message, index) => (
                                                <CheckboxInput
                                                    className="mt-[13px] md:mt-[30px]"
                                                    key={`terms-${index}`}
                                                    label={
                                                        <span className="tx-large-regular text-gray-1 dark:text-white">
                                                            {message}
                                                        </span>
                                                    }
                                                    checked={termsSelected?.[index]}
                                                    handleChange={() => {
                                                        selectTerms(index);
                                                        clearBackError();
                                                    }}
                                                />
                                            ))}
                                        {/* {(addCartHasBackError || (status.error && status.message)) && (
                                    <p
                                        className="tx-large-semibold mt-5 flex w-full cursor-pointer rounded-lg bg-state-error px-6 py-3 text-white underline"
                                        onClick={() => {
                                            scrollToId(status.link);
                                        }}
                                        data-test-id="ticket-error-btn"
                                    >
                                        <InfoIcon className=" mr-2 mt-[2px] h-4 w-4 min-w-[16px] [&_circle]:stroke-white [&_path]:fill-white" />
                                        {status.message || error.message}
                                    </p>
                                )} */}
                                        <div
                                            id="add-to-cart"
                                            className="relative mt-5 flex w-full items-center gap-x-3 sm:mt-8 sm:gap-x-5"
                                            data-test-id="add-to-cart-block"
                                        >
                                            <FormButton
                                                variant="outlined"
                                                className="mx-0 h-[49px] max-w-[50%] p-0"
                                                label={
                                                    currentAction === 'add_to_cart' &&
                                                    loading.state ? (
                                                        <SpinnerLoaderSecondary />
                                                    ) : (
                                                        'Add to cart'
                                                    )
                                                }
                                                disabled={loading.state || !status.isValid}
                                                onClick={handleAddToCart}
                                                data-test-id="add-to-cart-btn"
                                                tabIndex={0}
                                            />
                                            <FormButton
                                                className="mx-0 h-[49px] max-w-[50%] p-0"
                                                label={
                                                    currentAction === 'buy_now' && loading.state ? (
                                                        <SpinnerLoaderSecondary />
                                                    ) : (
                                                        'Buy Now'
                                                    )
                                                }
                                                disabled={loading.state || !status.isValid}
                                                onClick={handleBuyNow}
                                                data-test-id="buy-now-btn"
                                            />
                                        </div>
                                    </div>
                                    <FormDivider className="mb-5 mt-5 sm:mt-10" />
                                    <PaymentIcons isStripe={stripe_on} />
                                </div>
                            </div>
                        ) : (
                            <div
                                data-test-id="calendar-no-activities"
                                className="mt-11 h-full w-full max-w-full rounded-3xl bg-ash p-4 py-6 dark:bg-dark-3"
                            >
                                <h3 className="tx-heading-2 w-full text-center text-gray-1 md:tx-large-semibold dark:text-white">
                                    The selected trip time is unavailable, please try to choose
                                    another time or day
                                </h3>
                                <p className="tx-large-medium mx-auto mt-2 w-fit text-gray-3 dark:text-dark-1 md:hidden">
                                    Or
                                </p>
                                {/* <CallToBook className="mx-auto mt-2 flex w-[210px] px-3 md:w-full" /> */}
                                <div className="mx-auto mb-9 mt-12 flex h-[213px] w-full max-w-[224px] items-center justify-center bg-no-activities bg-contain bg-center bg-no-repeat dark:bg-no-activities-dark md:h-[130px]" />
                            </div>
                        )
                    ) : (
                        <TicketSkeleton />
                    )}
                </div>
            </div>
            {selectedDayFull && tickets?.length && (
                <StickyStatusBar
                    totalSum={totalSum}
                    partnerSlug={partner_slug.toString()}
                    setOptionsTouched={() => setOptionsTouched(true)}
                    includesTaxes={includesTaxes}
                    className="after:absolute after:left-[-1px] after:top-[-17px] after:h-[16px] after:w-[calc(100%+2px)] after:bg-gradient-transparency after:content-[''] after:dark:bg-gradient-transparency-dark md:bottom-2 md:rounded-[15px]"
                />
            )}
        </>
    );
};
